import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import { DefaultValues, FormProvider, useForm } from 'react-hook-form';
import { use, useCallback, useEffect, useState } from 'react';
import AppDataGrid, {
	initialPagination,
} from '@/common/components/dataGrid/AppDataGrid';
import StaffListSummaryFilterSection from './components/FieldCommonSummaryPageFilterSection';
import {
	Form,
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import qs from 'qs';
import { filterObjectByKeys, parseSummarySearchParams } from '@/utils/qsHelper';
import { FieldFilter } from '@/api/DTO/field-option/fieldOption.interface';
import {
	useGetFieldOptionCommonDetailQuery,
	useGetFieldOptionCommonIdDetailQuery,
	useGetFieldOptionCommonListQuery,
	useGetFieldOptionRegionDetailQuery,
	useGetFieldOptionRegionListQuery,
} from '@/api/field-option/fieldOption';
import dayjs from 'dayjs';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import { FormType } from '@/api/enum/formType.enum';
import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';
import DonutLargeOutlinedIcon from '@mui/icons-material/DonutLargeOutlined';
import EditRegionDialog from './components/EditFieldCommonDialog';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { PermissionEnum } from '@/api/enum/common.enum';
import { useGetUser } from '@/hooks/useGetUser';

interface Props {
	type: number;
}
export default function FieldCommonSummaryPage(props: Props) {
	const { type } = props;
	const { t } = useTranslation();
	const [searchParams, setSearchParams] = useSearchParams();
	const [isInitializedForm, setIsInitializedForm] = useState<boolean>(false);
	const navigate = useNavigate();
	const {
		fieldId,
		fieldType,
		agencyId,
		agencyType,
		referralId,
		referralType,
		diagnosisId,
		diagnosisType,
	} = useParams();
	console.log(fieldId, agencyId, referralId, diagnosisId);
	const { userInfo } = useGetUser();

	const { data: dictDetails } = useGetFieldOptionCommonDetailQuery(
		fieldType || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 1 && !fieldType,
		}
	);
	const { data: agencyDetails } = useGetFieldOptionCommonDetailQuery(
		agencyType || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 2 && !agencyType,
		}
	);
	const { data: referralDetails } = useGetFieldOptionCommonDetailQuery(
		referralType || '',
		{
			refetchOnMountOrArgChange: true,
			skip: type !== 3 && !referralType,
		}
	);
	const { data: agencyIdDetails } = useGetFieldOptionCommonIdDetailQuery(
		agencyId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !agencyId,
		}
	);
	const { data: referralIdDetails } = useGetFieldOptionCommonIdDetailQuery(
		referralId || '',
		{
			refetchOnMountOrArgChange: true,
			skip: !referralId,
		}
	);

	const defaultPropertyFilter: FieldFilter = {
		pagination: initialPagination,
		searchString: '',
		sortings: [],
	};
	const defaultValues: DefaultValues<FieldFilter> = defaultPropertyFilter;
	const methods = useForm<FieldFilter>({
		defaultValues,
	});
	const transformedSearchParams: FieldFilter = {
		...defaultPropertyFilter,
		...filterObjectByKeys(parseSummarySearchParams(searchParams), [
			'pagination',
			'sortings',
			'searchString',
		]),
	};

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetFieldOptionCommonListQuery(
			{
				...transformedSearchParams,
				type:
					type === 2
						? agencyType
						: type === 3
						? referralType
						: diagnosisType
						? diagnosisType
						: fieldType,
				parentId:
					type === 2
						? agencyId
						: type === 3
						? referralId
						: diagnosisId
						? diagnosisId
						: '',
				// parentId:
				// 	type === 2
				// 		? fieldId
				// 		: type === 3
				// 		? agencyId
				// 		: diagnosisId
				// 		? diagnosisId
				// 		: '',
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: any[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);

	// watch fields change
	useEffect(() => {
		const subscription = methods.watch((value) => {
			const propertyFilterFormResult = value as FieldFilter;

			const cleanedFilter = Object.fromEntries(
				Object.entries(propertyFilterFormResult).filter(([key, val]) => {
					return val !== '' && val != null;
				})
			);

			setSearchParams(qs.stringify(cleanedFilter));
		});
		return () => subscription.unsubscribe();
	}, [methods.watch]);

	// watch serach params
	useEffect(() => {
		if (!isInitializedForm) {
			methods.reset(transformedSearchParams, {
				keepDefaultValues: true,
			});
			setIsInitializedForm(true);
		}
	}, [searchParams]);

	const columns: GridColDef[] = [
		...(type === 1 && dictDetails?.data.hasCode
			? [
					{
						field: 'code',
						headerName: t('field.code'),
						flex: 20,
					},
			  ]
			: []),
		...(type === 2 && agencyDetails?.data.hasCode
			? [
					{
						field: 'code',
						headerName: t('field.code'),
						flex: 20,
					},
			  ]
			: []),
		...(type === 3 && referralDetails?.data.hasCode
			? [
					{
						field: 'code',
						headerName: t('field.code'),
						flex: 20,
					},
			  ]
			: []),
		...(type === 4 && dictDetails?.data.hasCode
			? [
					{
						field: 'code',
						headerName: t('field.code'),
						flex: 20,
					},
			  ]
			: []),
		{
			field: 'labelEn',
			headerName: t('field.en'),
			flex: 20,
		},

		...(type === 1 && dictDetails?.data.hasChinese
			? [
					{
						field: 'labelZh',
						headerName: t('field.zh'),
						flex: 20,
					},
			  ]
			: []),
		...(type === 2 && agencyDetails?.data.hasChinese
			? [
					{
						field: 'labelZh',
						headerName: t('field.zh'),
						flex: 20,
					},
			  ]
			: []),
		...(type === 3 && referralDetails?.data.hasChinese
			? [
					{
						field: 'labelZh',
						headerName: t('field.zh'),
						flex: 20,
					},
			  ]
			: []),
		...(type === 4 && dictDetails?.data.hasChinese
			? [
					{
						field: 'labelZh',
						headerName: t('field.zh'),
						flex: 20,
					},
			  ]
			: []),

		...(fieldType === 'nature'
			? [
					{
						field: 'needToEnterRiskLevel',
						headerName: t('field.needEnterRiskLevel'),
						flex: 20,
						renderCell: (params: any) =>
							params.row.needToEnterRiskLevel ? 'Yes' : 'No',
					},
			  ]
			: []),
		...(fieldType === 'nature'
			? [
					{
						field: 'countAsAMeetingIntervention',
						headerName: t('field.countMeetingIntervention'),
						flex: 20,
						renderCell: (params: any) =>
							params.row.needToEnterRiskLevel ? 'Yes' : 'No',
					},
			  ]
			: []),
		...(fieldType === 'nature'
			? [
					{
						field: 'countAsAProgramMemberRenewalIntervention',
						headerName: t('field.countProgramMemberRenewalIntervention'),
						flex: 20,
						renderCell: (params: any) =>
							params.row.needToEnterRiskLevel ? 'Yes' : 'No',
					},
			  ]
			: []),

		{
			field: 'updatedAt',
			headerName: t('field.lastUpdated'),
			flex: 20,
		},

		{
			field: 'status',
			headerName: t('field.status'),
			flex: 20,
			renderCell: (params) => (
				<Chip
					color={params.row.status === 0 ? 'chipTertiary' : 'chipPrimary'}
					sx={{
						height: 'auto',
						minHeight: '24px',
						maxWidth: '100%',
						overflow: 'hidden',
						whiteSpace: 'normal',
						wordBreak: 'break-word',
						padding: '4px 4px',
					}}
					label={
						<Box
							sx={{
								width: '100%',
								whiteSpace: 'normal',
								wordBreak: 'break-word',
							}}
						>
							{params.row.status === 0
								? t('field.active')
								: t('field.inActive')}
						</Box>
					}
				/>
			),
		},
		{
			field: 'actionedByName',
			headerName: t('field.actionedBy'),
			flex: 20,
		},
		{
			field: 'edit',
			headerName: '',
			width: 68,
			renderCell: (params) => (
				<EditRegionDialog
					currentId={params.row.id}
					formType={FormType.edit}
					parentId={type === 2 ? fieldId : type === 3 ? agencyId : ''}
					type={type === 2 ? agencyType : type === 3 ? referralType : fieldType}
				/>
			),
		},
		{
			field: 'view',
			headerName: '',
			width: 68,
			renderCell: (params) =>
				type === 1 && dictDetails?.data.childType ? (
					<Link
						to={`/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${params.row.id}/${dictDetails?.data.childType}`}
						state={{
							propertySummarySearchParams: qs.stringify(
								transformedSearchParams
							),
						}}
					>
						<IconButton color='secondary'>
							<VisibilityOutlinedIcon />
						</IconButton>
					</Link>
				) : type === 2 && agencyDetails?.data.childType ? (
					<Link
						to={`/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}/referral/${params.row.id}/${agencyDetails?.data.childType}`}
						state={{
							propertySummarySearchParams: qs.stringify(
								transformedSearchParams
							),
						}}
					>
						<IconButton color='secondary'>
							<VisibilityOutlinedIcon />
						</IconButton>
					</Link>
				) : type === 4 &&
				  dictDetails?.data.childType === 'main_sec_diagnosis' ? (
					<Link
						to={`/field-management/field-management-list/field/${fieldId}/${fieldType}/diagnosis/${params.row.id}/${dictDetails?.data.childType}`}
						state={{
							propertySummarySearchParams: qs.stringify(
								transformedSearchParams
							),
						}}
					>
						<IconButton color='secondary'>
							<VisibilityOutlinedIcon />
						</IconButton>
					</Link>
				) : (
					''
				),
		},
	];

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>
					{type === 2
						? `${agencyDetails?.data.labelDisplay} (${agencyIdDetails?.data.labelEn})`
						: type === 3
						? `${referralDetails?.data.labelDisplay} (${referralIdDetails?.data.labelEn})`
						: dictDetails?.data.labelDisplay}
				</Typography>
			</Box>
			<Box marginTop={1}>
				{userInfo?.permissionList?.includes(
					PermissionEnum.OptionManagementManageStatus
				) && (
					<Button
						endIcon={<DonutLargeOutlinedIcon />}
						onClick={() => {
							navigate(
								type === 2 && agencyType
									? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}/status`
									: type === 3 && referralType
									? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}/referral/${referralId}/${referralType}/status`
									: `/field-management/field-management-list/field/${fieldId}/${fieldType}/status`
							);
						}}
						variant='contained'
						sx={{ marginRight: '12px' }}
					>
						{t('field.status')}
					</Button>
				)}

				{userInfo?.permissionList?.includes(
					PermissionEnum.OptionManagementReOrder
				) && (
					<Button
						endIcon={<SwapVertOutlinedIcon />}
						onClick={() => {
							navigate(
								type == 2 && agencyType
									? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}/order`
									: type === 3 && referralType
									? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}/referral/${referralId}/${referralType}/order`
									: `/field-management/field-management-list/field/${fieldId}/${fieldType}/order`
							);
						}}
						variant='contained'
						sx={{ marginRight: '12px' }}
					>
						{t('field.reOrderOption')}
					</Button>
				)}

				{fieldType != 'nature' && (
					<EditRegionDialog
						formType={FormType.create}
						parentId={type === 2 ? fieldId : type === 3 ? agencyId : ''}
						type={
							type === 2 ? agencyType : type === 3 ? referralType : fieldType
						}
					/>
				)}
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<StaffListSummaryFilterSection></StaffListSummaryFilterSection>

						<Box marginTop={2}>
							<AppDataGrid
								loading={!isGetPropertySummarySuccess}
								rows={transformResponse(propertySummary?.data?.items) ?? []}
								rowCount={propertySummary?.data?.pagination.totalItems ?? 0}
								columns={columns}
								getRowId={(row) => row.id}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
				<Button
					type='submit'
					variant='contained'
					onClick={() => {
						navigate(
							type === 2
								? `/field-management/field-management-list/field/${fieldId}/${fieldType}`
								: type === 3
								? `/field-management/field-management-list/field/${fieldId}/${fieldType}/agency/${agencyId}/${agencyType}`
								: `/field-management/field-management-list`
						);
					}}
				>
					{t('button.back')}
				</Button>
			</Box>
		</>
	);
}
